module Client.Page.User.MyTalgilTriggers

open Client
open Client.Components
open Client.Domain
open Client.DomainTypes
open Client.DomainTypes.Msg
open Client.DomainTypes.MyTalgilTriggers
open Client.Forms
open Client.Infrastructure
open Client.Infrastructure.Api
open Elmish
open Fable.FontAwesome
open Fable.React
open Fable.React.Props
open Feliz
open Fulma
open Fulma.Extensions.Wikiki
open Shared
open Shared.Dto
open Shared.Dto.Dto
open Shared.Dto.Form
open Shared.Dto.Page.User
open Shared.Infrastructure
open Thoth.Elmish

let private errorConfirmationConfig: ErrorConfirmation.Config<Msg> = {
    Handler = ErrorConfirmation.createHandler (Reload >> MyTalgilTriggers)
    TimeoutMilliseconds = 5000
}

type DataModel = {
    Triggers: TriggerRowViewModel list
    Sensors: IdValue<SoilMapSensorDto> list
    Session: UserSession
    DeleteModal: ConfirmationModal.Model<TriggerRowViewModel> option
    CreationForm: TalgilTriggerForm.Model option
    RequestRunning: bool
}

type LoadingModel = {
    Session: UserSession
    Triggers: MyTalgilTriggers.ListingDto list option
    Sensors: IdValue<SoilMapSensorDto> list option
}

type Model = Loadable<DataModel, LoadingModel>

let private dtoToViewModel (sensors: IdValue<SoilMapSensorDto> list) (dto: MyTalgilTriggers.ListingDto) =
    let maybeSensor =
        List.tryFind (fun (s: IdValue<SoilMapSensorDto>) -> s.Id = dto.Trigger.SensorId) sensors

    match maybeSensor with
    | Some sensor -> {
        Id = dto.Trigger.Id
        Controller = sprintf "%s (%d)" dto.Trigger.ControllerName dto.Trigger.ControllerId
        Sensor = sensor.Value.Base.Name
        DryLimit = sensor.Value.DryLimit
        Program = dto.Trigger.Program.Label
        IsActive = dto.Trigger.IsActive
        LastExecution = dto.LastExecution
        RequestRunning = false
      }
    | None -> {
        Id = dto.Trigger.Id
        Controller = sprintf "%s (%d)" dto.Trigger.ControllerName dto.Trigger.ControllerId
        Sensor = "Unbekannter Sensor"
        DryLimit = None
        Program = dto.Trigger.Program.Label
        IsActive = dto.Trigger.IsActive
        LastExecution = dto.LastExecution
        RequestRunning = false
      }


let init (session: UserSession) =
    let requestData = {
        SessionKey = session.SessionKey
        Data = ()
    }

    let cmd =
        Cmd.batch [
            Cmd.OfAsync.perform
                api.getMyTalgilTriggers
                requestData
                (MyTalgilTriggersMsg.TriggersReceived >> MyTalgilTriggers)
            Cmd.OfAsync.perform
                api.getMySoilSensors
                requestData
                (MyTalgilTriggersMsg.SoilSensorsReceived >> MyTalgilTriggers)
        ]

    Loadable.Loading {
        Session = session
        Triggers = None
        Sensors = None
    },
    cmd

let private tryCreateDataModel (loadingModel: LoadingModel) =
    match loadingModel.Triggers, loadingModel.Sensors with
    | Some triggers, Some sensors ->
        Loadable.Data {
            DataModel.Triggers = List.map (dtoToViewModel sensors) triggers
            Session = loadingModel.Session
            Sensors = sensors
            DeleteModal = None
            CreationForm = None
            RequestRunning = false
        }
    | _, _ -> Loadable.Loading loadingModel

let private handleProgramStarted
    (triggerId: int)
    (response: AuthenticatedResponse<Talgil.TriggerExecutionDto>)
    (model: DataModel)
    =
    match response with
    | Ok lastExecution ->
        let updatedTriggers =
            model.Triggers
            |> List.filteredMap
                (fun trigger -> trigger.Id = triggerId)
                (fun trigger -> {
                    trigger with
                        LastExecution = Some lastExecution
                        RequestRunning = false
                })

        let toast =
            match lastExecution.ExecutionResult.Type with
            | Talgil.ExecutionResultTypeDto.Success ->
                Toast.create "Das Programm wurde erfolgreich gestartet" |> Toast.success
            | Talgil.ExecutionResultTypeDto.Info -> Toast.create "Das Programm wurde nicht gestartet" |> Toast.info
            | Talgil.ExecutionResultTypeDto.Error ->
                Toast.create "Das Programm konnte nicht gestartet werden" |> Toast.error

        Loadable.Data {
            model with
                Triggers = updatedTriggers
                RequestRunning = false
        },
        toast
    | Result.Error err ->
        let cmd = ErrorConfirmation.responseError errorConfirmationConfig err

        Loadable.Data model, cmd

let deleteConfig dispatch (trigger: TriggerRowViewModel) : ConfirmationModal.Configuration =
    let text =
        sprintf "Sollen der Trigger für das Talgil-Program '%s' wirklich gelöscht werden?" trigger.Program

    {
        Headline = "Trigger löschen"
        Text = text
        OnClose = (fun _ -> dispatch CloseDeleteTriggerModal)
        OnYes = (fun _ -> dispatch (DeleteConfirmation trigger.Id))
        OnNo = (fun _ -> dispatch CloseDeleteTriggerModal)
    }

let private updateSingleTrigger
    (f: TriggerRowViewModel -> TriggerRowViewModel)
    (triggerId: int)
    (model: DataModel)
    : DataModel =
    let updatedTriggers =
        model.Triggers |> List.filteredMap (fun row -> row.Id = triggerId) f

    { model with Triggers = updatedTriggers }


let update (msg: MyTalgilTriggersMsg) (model: Model) =
    match msg, model with
    | MyTalgilTriggersMsg.TriggersReceived response, Loadable.Loading model ->
        match response with
        | Result.Ok triggers -> tryCreateDataModel { model with Triggers = Some triggers }, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | MyTalgilTriggersMsg.SoilSensorsReceived response, Loadable.Loading model ->
        match response with
        | Result.Ok sensors -> tryCreateDataModel { model with Sensors = Some sensors }, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | MyTalgilTriggersMsg.StartProgram id, Loadable.Data model ->
        let requestData = {
            SessionKey = model.Session.SessionKey
            Data = id
        }

        let newModel =
            { model with RequestRunning = true }
            |> updateSingleTrigger (fun row -> { row with RequestRunning = true }) id

        let cmd =
            Cmd.OfAsync.perform
                api.startTalgilProgram
                requestData
                (fun response -> MyTalgilTriggersMsg.ProgramStarted(id, response) |> MyTalgilTriggers)

        Loadable.Data newModel, cmd
    | ProgramStarted(id, response), Loadable.Data model -> handleProgramStarted id response model
    | MyTalgilTriggersMsg.SetIsActive msg, Loadable.Data model ->
        let request = {
            SessionKey = model.Session.SessionKey
            Data = msg
        }

        let cmd =
            Cmd.OfAsync.perform
                api.setIsActiveTalgilTrigger
                request
                (fun response -> (msg, response) |> MyTalgilTriggersMsg.IsActiveChanged |> MyTalgilTriggers)

        let updatedTriggers =
            model.Triggers
            |> List.filteredMap (fun row -> row.Id = msg.TriggerId) (fun row -> { row with RequestRunning = true })

        Loadable.Data {
            model with
                RequestRunning = true
                Triggers = updatedTriggers
        },
        cmd
    | MyTalgilTriggersMsg.IsActiveChanged(dto, response), Loadable.Data model ->
        match response with
        | Ok _ ->
            let newModel =
                { model with RequestRunning = false }
                |> updateSingleTrigger
                    (fun row -> {
                        row with
                            RequestRunning = false
                            IsActive = dto.IsActive
                    })
                    dto.TriggerId

            let toastMsg =
                match dto.IsActive with
                | true -> "Trigger erfolgreich aktiviert"
                | false -> "Trigger erfolgreich deaktiviert"

            let cmd = Toast.create toastMsg |> Toast.success

            Loadable.Data newModel, cmd
        | Result.Error err ->
            let cmd = ErrorConfirmation.responseError errorConfirmationConfig err

            let newModel =
                { model with RequestRunning = false }
                |> updateSingleTrigger (fun row -> { row with RequestRunning = false }) dto.TriggerId

            Loadable.Data newModel, cmd
    | MyTalgilTriggersMsg.DeleteTrigger trigger, Loadable.Data model ->
        let modalModel: ConfirmationModal.Model<TriggerRowViewModel> = {
            Data = trigger
            RequestRunning = false
        }

        let newModel = { model with DeleteModal = Some modalModel }

        Loadable.Data newModel, Cmd.none
    | MyTalgilTriggersMsg.DeleteConfirmation id, Loadable.Data model ->
        let requestData = {
            SessionKey = model.Session.SessionKey
            Data = id
        }

        let cmd =
            Cmd.OfAsync.perform
                api.deleteTalgilTrigger
                requestData
                (fun response -> (id, response) |> MyTalgilTriggersMsg.TriggerDeleted |> MyTalgilTriggers)

        let newDeleteModalModel =
            model.DeleteModal
            |> Option.map (fun modalModel -> { modalModel with RequestRunning = true })

        Loadable.Data { model with DeleteModal = newDeleteModalModel }, cmd
    | MyTalgilTriggersMsg.TriggerDeleted(id, response), Loadable.Data model ->
        match response with
        | Result.Ok(true) ->
            let filteredTrigger =
                model.Triggers |> List.filter (fun trigger -> trigger.Id <> id)

            let cmd = Toast.create "Talgil Trigger wurde erfolgreich gelöscht" |> Toast.success

            Loadable.Data {
                model with
                    Triggers = filteredTrigger
                    DeleteModal = None
            },
            cmd
        | _ ->
            let cmd = Toast.create "Beim Löschen ist ein Fehler aufgetreten" |> Toast.error

            let newDeleteModalModel =
                model.DeleteModal
                |> Option.map (fun modalModel -> { modalModel with RequestRunning = false })

            Loadable.Data { model with DeleteModal = newDeleteModalModel }, cmd
    | MyTalgilTriggersMsg.CloseDeleteTriggerModal, Loadable.Data model ->
        Loadable.Data { model with DeleteModal = None }, Cmd.none
    | MyTalgilTriggersMsg.TalgilTriggerFormMsg(TalgilTriggerFormMsg.TriggerCreated response), Loadable.Data model ->
        match response with
        | Result.Ok trigger ->
            let newList =
                (dtoToViewModel model.Sensors trigger) :: model.Triggers
                |> List.sortBy (fun trigger -> trigger.Controller, trigger.Program)

            let cmd = Toast.create "Der Trigger wurde erfolgreich erstellt" |> Toast.success

            Loadable.Data {
                model with
                    CreationForm = None
                    Triggers = newList
            },
            cmd
        | Result.Error err ->
            let cmd = ErrorConfirmation.responseError errorConfirmationConfig err

            let newForm =
                model.CreationForm
                |> Option.map (fun form -> { form with CreateRequestRunning = false })

            Loadable.Data { model with CreationForm = newForm }, cmd
    | MyTalgilTriggersMsg.TalgilTriggerFormMsg msg, Loadable.Data model ->
        match model.CreationForm with
        | Some form ->
            let formModel, formCmd = TalgilTriggerForm.update msg form

            let cmd =
                Cmd.map (MyTalgilTriggersMsg.TalgilTriggerFormMsg >> MyTalgilTriggers) formCmd

            Loadable.Data { model with CreationForm = Some formModel }, cmd
        | None -> Loadable.Data model, Cmd.none
    | MyTalgilTriggersMsg.OpenCreateModal, Loadable.Data model ->
        let formModel =
            model.Sensors
            |> List.map (fun sensor -> IdValue.map MapSensorDto.Soil sensor)
            |> TalgilTriggerForm.init model.Session

        Loadable.Data { model with CreationForm = Some formModel }, Cmd.none
    | MyTalgilTriggersMsg.CloseCreateModal, Loadable.Data model ->
        Loadable.Data { model with CreationForm = None }, Cmd.none
    | MyTalgilTriggersMsg.Reload reload, _ ->
        ErrorConfirmation.doReload reload

        model, Cmd.none
    | _, _ -> model, Cmd.none

let private createSuccesFullCell (last: Talgil.TriggerExecutionDto) =
    let content =
        match last.ExecutionResult.Type with
        | Talgil.ExecutionResultTypeDto.Success -> Icon.createWithText Fa.Solid.CheckCircle last.ExecutionResult.Message
        | Talgil.ExecutionResultTypeDto.Info ->
            Icon.Text.span [
                GenericOption.Props [
                    Class Tooltip.ClassName
                    Tooltip.dataTooltip last.ExecutionResult.Message
                ]
            ] [
                Icon.icon [] [ Fa.i [ Fa.Solid.InfoCircle ] [] ]
                span [] [ str "Programm wurde nicht gestartet" ]
            ]
        | Talgil.ExecutionResultTypeDto.Error ->
            Icon.Text.span [
                GenericOption.Props [
                    Class Tooltip.ClassName
                    Tooltip.dataTooltip last.ExecutionResult.Message
                ]
            ] [
                Icon.icon [] [ Fa.i [ Fa.Solid.TimesCircle ] [] ]
                span [] [ str "Fehlgeschlagen" ]
            ]

    td [] [ content ]

let private createLastExecutionTimestampCell (last: Talgil.TriggerExecutionDto) =
    let text =
        last.ExecutionTime
        |> DateTimeOffset.toLocalDateTime
        |> DateTime.toDateTimeString

    td [] [ Icon.createWithText Fa.Solid.Clock text ]

let private createLastExecutionSourceCell (last: Talgil.TriggerExecutionDto) =
    let content =
        match last.TriggerSource with
        | Talgil.SensorData data -> "Sensor-Daten: " + (MeasuredValueString.percentageToString data.SoilMoisture)
        | Talgil.Manual -> "Manuell"

    td [] [ str content ]

let private createLastExecutionCells (last: Talgil.TriggerExecutionDto) : ReactElement list =

    [
        createLastExecutionTimestampCell last
        createLastExecutionSourceCell last
        createSuccesFullCell last
    ]

let private createDeleteButton dispatch (disableButton: bool) (trigger: TriggerRowViewModel) =
    let buttonOptions = [
        Button.IsLoading trigger.RequestRunning
        Button.Disabled disableButton
    ]

    CommonButtons.createDeleteButton
        buttonOptions
        "Löschen"
        (fun _ -> trigger |> MyTalgilTriggersMsg.DeleteTrigger |> MyTalgilTriggers |> dispatch)

let private createShowLogButton dispatch (trigger: TriggerRowViewModel) =
    let route = Route.TalgilTriggerExecutionLog trigger.Id

    CommonButtons.createTooltipIconLink
        [ Button.Props[Href(Routing.routeToUrl route)] ]
        Fa.Solid.List
        "Protokoll anzeigen"
        (Clickable.onClickGoToRoute dispatch route)

let private createStartProgramManuallyButton dispatch (disabled: bool) (trigger: TriggerRowViewModel) =
    let buttonOptions = [
        Button.Color IsLink
        Button.Disabled disabled
        Button.IsLoading trigger.RequestRunning
    ]

    CommonButtons.createTooltipIconButton
        buttonOptions
        Fa.Solid.Play
        "Programm manuell starten"
        (fun _ -> trigger.Id |> MyTalgilTriggersMsg.StartProgram |> MyTalgilTriggers |> dispatch)

let private createSetIsActiveButton dispatch (disableButton: bool) (trigger: TriggerRowViewModel) =
    let buttonOptions = [
        Button.IsLoading trigger.RequestRunning
        Button.Disabled disableButton
        Button.OnClick(fun _ ->
            MyTalgilTriggersMsg.SetIsActive {
                TriggerId = trigger.Id
                IsActive = not trigger.IsActive
            }
            |> MyTalgilTriggers
            |> dispatch
        )
    ]

    let text = if trigger.IsActive then "Deaktiveren" else "Aktivieren"

    Button.button buttonOptions [ str text ]

let private triggerToRow dispatch (requestRunning: bool) (index: int) (row: TriggerRowViewModel) : ReactElement =
    let disableButtons = requestRunning && (not row.RequestRunning)

    tr [ Class "v_centered" ] [
        td [] [ Table.rowIndexString index ]
        td [] [ str row.Controller ]
        td [] [ str row.Program ]
        td [] [ str row.Sensor ]
        td [] [
            match row.DryLimit with
            | Some dryLimit -> MeasuredValueString.percentageToString dryLimit |> str
            | None -> ()
        ]

        td [ Class "sensor-status" ] [
            span [] [ IsActiveComponent.toText row.IsActive |> str ]

            img [
                StatusDot.isActiveToImageUrl row.IsActive |> Src
            ]

            createSetIsActiveButton dispatch disableButtons row
        ]

        match row.LastExecution with
        | Some last -> yield! createLastExecutionCells last
        | None -> td [ ColSpan 3 ] []

        td [] [
            div [
                Class "is-flex is-justify-content-flex-end is-3"
                Style [ GridGap "0.5em" ]
            ] [
                createStartProgramManuallyButton dispatch disableButtons row
                createShowLogButton dispatch row
                createDeleteButton dispatch disableButtons row
            ]
        ]
    ]

let private table dispatch (model: DataModel) =
    Table.table [ Table.IsFullWidth ] [
        thead [] [
            tr [] [
                th [] [ str "#" ]
                th [] [ str "Controller" ]
                th [] [ str "Programm" ]
                th [] [ str "Sensor" ]
                th [] [ str "Trockengrenze" ]
                th [] [ str "Status" ]
                th [] [ str "Letzter Start - Zeitpunkt" ]
                th [] [ str "Letzter Start - Grund" ]
                th [] [ str "Letzter Start - Erfolgreich" ]
                th [] []
            ]
        ]

        tbody [] (List.mapi (triggerToRow dispatch model.RequestRunning) model.Triggers)
    ]

let private createModal dispatch (model: TalgilTriggerForm.Model) =
    let modalProps: Modal.ModalProps = {
        OnClose = (fun _ -> MyTalgilTriggersMsg.CloseCreateModal |> dispatch)
        Headline = "Neuer Talgil-Programm Trigger erstellen"
        Body = [
            TalgilTriggerForm.view (MyTalgilTriggersMsg.TalgilTriggerFormMsg >> dispatch) model
        ]
        Footer = TalgilTriggerForm.buttonView (MyTalgilTriggersMsg.TalgilTriggerFormMsg >> dispatch) model
    }

    Modal.view modalProps

let createNewTriggerButton dispatch =
    Button.button [
        Button.Color IsLink
        Button.OnClick(fun _ -> dispatch (MyTalgilTriggers MyTalgilTriggersMsg.OpenCreateModal))
    ] [ str "Neuer Trigger erstellen" ]

let private tableButtons dispatch =
    Level.level [] [
        Level.left [] []
        Level.right [] [
            Level.item [] [
                Field.div [] [
                    Control.div [] [ createNewTriggerButton dispatch ]
                ]
            ]
        ]
    ]

let private hints: ReactElement =
    Html.span [
        prop.className [ "has-text-grey"; "block" ]
        prop.text
            "Damit MySens ein Talgil-Programm auf deinem Controller starten darf, musst du uns den Zugriff auf deinen Controller freischalten lassen. Melde dich dazu bitte bei dem Händler bei dem du die Steuerung gekauft hast."
    ]

let private dataView dispatch (model: DataModel) =
    Container.container [ Container.IsFluid ] [
        Heading.h1 [] [ str "Meine Talgil-Programm-Trigger" ]

        tableButtons dispatch

        table dispatch model

        hints

        match model.CreationForm with
        | Some form -> createModal (MyTalgilTriggers >> dispatch) form
        | None -> ()

        match model.DeleteModal with
        | Some model ->
            let config = deleteConfig (MyTalgilTriggers >> dispatch) model.Data

            ConfirmationModal.view model.RequestRunning config
        | None -> ()
    ]

let view dispatch (model: Model) = Loadable.view (dataView dispatch) model